/**
 * Shared Plan System Constants
 *
 * These values define the plan system behavior across all apps.
 * Changes here affect both frontend and backend.
 */

import { normalizeLocale } from '../locale'

/** Number of days for default trial period */
export const DEFAULT_TRIAL_DAYS = 14

/** Days before expiration to show "expiring soon" warning */
export const PRO_EXPIRING_SOON_DAYS = 15

/** Quota usage percentage threshold for warning (0-1 scale, 0.8 = 80%) */
export const QUOTA_WARNING_THRESHOLD = 0.8

/** Maximum templates for free tier */
export const FREE_MAX_TEMPLATES = 2

/**
 * Monthly generation quota a **new** tenant is created with.
 *
 * Not "the Free quota". The entitlement is whatever is stored in
 * `tenants.monthly_quota`, and tenants created before this dropped from 100
 * keep the number they have — nothing rewrites it. So this is only correct in
 * two places: the insert in ensureTenantExists(), and copy read by someone who
 * has no tenant yet (the marketing site). Anywhere a tenant is known, show its
 * own `monthly_quota`; using this instead tells an older tenant it has less
 * than the gate actually allows.
 */
export const FREE_MONTHLY_QUOTA = 30

/**
 * What ensureTenantExists() wrote for a new tenant until FREE_MONTHLY_QUOTA
 * dropped (14 October 2026). A Free tenant still holding exactly this number is
 * on the old allowance and has not been moved yet — see `isLegacyFreeTenant()`,
 * and scripts/gift-free-packs.ts, which moves exactly those tenants.
 *
 * Reserved: an operator who wants to give a tenant a larger allowance must pick
 * another number, or the tenant reads as legacy Free (no footer, write-backs
 * kept) and the gift script would move it.
 */
export const OLD_FREE_MONTHLY_QUOTA = 100

/**
 * When the older Free tenants are moved to FREE_MONTHLY_QUOTA with a gift pack
 * (16 November 2026, 00:00 UTC). Only the announcement reads it — what a tenant
 * gets is decided by its own row (`isLegacyFreeTenant()`), never by the
 * calendar, so a late or early run of the gift script is what actually counts.
 */
export const FREE_PLAN_CHANGE_AT = Date.UTC(2026, 10, 16)

/**
 * Generations per minute allowed to a tenant without Pro access.
 *
 * The limit is *enforced* by the FREE_TIER_RATE_LIMITER binding in
 * apps/api/worker/wrangler.jsonc — Cloudflare's rate limiter takes its ceiling
 * from config, not from code. This constant is the number quoted back to the
 * user, and exists so the message cannot contradict the binding again: the two
 * were 1 and 2 respectively until it was noticed. Change both together.
 */
export const FREE_TIER_RATE_LIMIT_PER_MINUTE = 2

/** Maximum templates for trial tier */
export const TRIAL_MAX_TEMPLATES = 10

/** Base template allowance for the Pro plan (before any Extra Template add-ons) */
export const PRO_MAX_TEMPLATES = 10

/**
 * Days Pro keeps generated documents, at least — see
 * `effectiveGenerationRetentionDays()`. A seat does not raise it: with
 * priority support, it is what Pro has that a seat does not.
 */
export const PRO_GENERATION_RETENTION_DAYS = 7

/** Most Extra Templates a single purchase request may add */
export const MAX_EXTRA_TEMPLATES_PER_REQUEST = 50

/** Ceiling on a tenant's total Extra Templates add-on */
export const MAX_EXTRA_TEMPLATES_TOTAL = 500

/**
 * Documents a unit of the **retired** Generation Top-Up granted.
 *
 * The product is no longer sold, but its price id is still honoured: Paddle
 * mails payment links that stay payable, and a transaction for the old price
 * must grant what that customer was quoted — a pack of this size, valid
 * `TOPUP_EXPIRY_DAYS`. That is the only use left.
 */
export const GENERATIONS_PER_TOPUP = 100

/**
 * Validity, in days, of a pack granted through the retired top-up price. The
 * worker prefers the GENERATION_TOPUP_EXPIRY_DAYS binding; this is the fallback.
 */
export const TOPUP_EXPIRY_DAYS = 90

/**
 * Sizes a document pack is sold in. A pack is a one-time purchase drawn on once
 * the free monthly allowance, or the Business automation allowance, is used up.
 */
export const DOCUMENT_PACK_SIZES = [1000, 5000, 50000] as const
export type DocumentPackSize = (typeof DOCUMENT_PACK_SIZES)[number]

/** Days a document pack stays valid from the moment it is granted. */
export const PACK_VALIDITY_DAYS = 365

/**
 * The same validity as customers are quoted it. Its own constant rather than
 * `PACK_VALIDITY_DAYS` divided by something, because "12 months" is the
 * promise and 365 days is how the API keeps it; constants.test.ts holds the
 * two together.
 */
export const PACK_VALIDITY_MONTHS = 12

/** Ceiling on one operator-issued pack — a typo guard, not a product limit. */
export const MAX_MANUAL_PACK_SIZE = 1_000_000

/**
 * Size of the pack gifted to a Free tenant whose monthly allowance is brought
 * down to `FREE_MONTHLY_QUOTA` (scripts/gift-free-packs.ts).
 */
export const GIFT_PACK_SIZE = 1000

/**
 * Automated documents a Business seat includes per year — HTTP trigger and
 * approval-event runs. The allowance is tenant-wide: this times the Business
 * seats in force. See `automationAllowance()`.
 */
export const AUTOMATION_DOCS_PER_BUSINESS_SEAT = 20000

/**
 * Test documents a tenant may generate free per UTC day — approval tests and
 * the designer's "Preview with real data" on a Base template, from one shared
 * allowance.
 *
 * A test costs nothing so a template can be tried before anyone depends on it;
 * the cap is what stops "test" being a free printer for real documents. Past it
 * a test is charged like any other document.
 */
export const FREE_TESTS_PER_DAY = 20

/**
 * Ceiling on the seats one subscription may hold. The quantity is PATCHed to
 * Paddle and billed on the spot, so an unbounded value is an unbounded charge.
 */
export const MAX_SEATS_PER_SUBSCRIPTION = 500

/**
 * Paddle subscription statuses that still grant their entitlement.
 *
 * `past_due` is included on purpose: Paddle retries a failed card for days, and
 * cutting a paying customer off mid-retry is worse than carrying them. Paddle
 * moves the subscription to `canceled` when it gives up.
 *
 * Lives here rather than in planChecker so entitlements.ts can read it without
 * importing planChecker, which now depends on entitlements.
 */
export const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due'] as const

/** Milliseconds in a day */
export const MS_PER_DAY = 86400000

/**
 * AutomaDoc documentation site (apps/docs) — production.
 *
 * Deliberately not exported: it is the fallback inside `docsUrl()`, and every
 * caller goes through that so it can pass its environment's origin. An exported
 * constant is an invitation to link the production host from a staging build,
 * which is the bug the `origin` parameter exists to prevent. The bare root is
 * also the wrong target regardless of environment — it sniffs Accept-Language,
 * ignoring the language the user picked inside our apps.
 */
const DOCS_URL = 'https://docs.automadoc.cc'

/**
 * Trim a configured origin to the form the builders below concatenate onto.
 *
 * An empty or missing value falls back to production rather than producing
 * `undefined/en/...`: an unset variable should degrade to a working link.
 */
function docsOrigin(origin?: string | null): string {
	const trimmed = origin?.trim().replace(/\/+$/, '')
	return trimmed || DOCS_URL
}

/** The docs have no landing page — `/` and `/<locale>/` both resolve here. */
export const DOCS_LANDING_PATH = 'start/what-is-automadoc'

/**
 * Build a link into the docs for a given page and locale.
 *
 * Pass the locale the surrounding app is currently displaying. It is run
 * through normalizeLocale(), so regional tags ('zh-CN'), Lark's legacy
 * Indonesian code ('in') and unknown values all resolve to a locale the docs
 * actually publish. Untranslated pages fall back to English on the docs side,
 * so naming a locale is always safe.
 *
 * `origin` overrides the host for non-production builds. It is a parameter
 * rather than something read here because this module is imported by the
 * Workers too, where `import.meta.env` does not exist — so each app passes its
 * own configured value and this stays environment-agnostic.
 */
export function docsUrl(path: string = DOCS_LANDING_PATH, locale?: string | null, origin?: string | null): string {
	const clean = path.replace(/^\/+|\/+$/g, '')
	return `${docsOrigin(origin)}/${normalizeLocale(locale)}/${clean}/`
}

/** Getting-started guide — linked from the company portal's onboarding card. */
export function docsGettingStartedUrl(locale?: string | null, origin?: string | null): string {
	return docsUrl('start/getting-started', locale, origin)
}

/**
 * The company portal (apps/company).
 *
 * Linkable from the public site because the portal now signs people in through
 * Lark's web OAuth rather than only working inside the Lark client — before
 * that, sending a visitor here from marketing produced a dead end.
 *
 * No locale in the path: the portal is a hash-routed SPA that picks its
 * language from the visitor's own stored preference, so unlike docsUrl() there
 * is nothing useful to put in the URL.
 */
export const COMPANY_PORTAL_URL = 'https://company.automadoc.cc'

/**
 * The marketing site (apps/www), which also hosts the legal policies.
 *
 * `www`, not the apex: that is the only hostname apps/www/worker claims a route
 * for. Like the portal above, this is the production fallback for apps that
 * resolve their own per-environment origin — it exists so the host is written
 * down once rather than retyped at each fallback.
 */
export const WWW_URL = 'https://www.automadoc.cc'

/**
 * The public page where anyone checks a signed PDF against its envelope (apps/www
 * `/<lang>/verify`), in the given language. `origin` is the environment's www origin (the
 * API's WWW_URL var); unset falls back to production, as docsUrl() does.
 */
export function esignVerifyUrl(locale?: string | null, origin?: string | null): string {
	return wwwPageUrl('verify', locale, origin)
}

/**
 * AutomaDoc Sign's product page (apps/www `/<lang>/sign`), in the given language, on the
 * environment's www origin; unset falls back to production, as esignVerifyUrl() does.
 */
export function esignAboutUrl(locale?: string | null, origin?: string | null): string {
	return wwwPageUrl('sign', locale, origin)
}

function wwwPageUrl(page: string, locale?: string | null, origin?: string | null): string {
	const host = origin?.trim().replace(/\/+$/, '') || WWW_URL
	return `${host}/${normalizeLocale(locale)}/${page}`
}

/** AutomaDoc Support Group invite link */
export const SUPPORT_GROUP_URL = 'https://applink.larksuite.com/client/chat/chatter/add_by_link?link_token=c2bm5e80-6820-4f74-92b7-282c21usm43i'
